/**
 * Calculate wrist rotation angle based on hand orientation.
 * Returns atan2(palmNormal.x, palmNormal.z), always in [-π, π]:
 *   near ±π → dorsal  (back of hand faces camera, palm normal points away)
 *   near  0 → palmar  (palm faces camera, palm normal points toward camera)
 *
 * Uses a cross product of forearm and knuckle vectors to get the palm normal.
 * No angle unwrapping — raw atan2 is correct for dorsal/palmar detection.
 */
export const calculateWristRotation = (
  keypoints3D: any,
): number | undefined => {
  if (!keypoints3D) return undefined;

  const wrist = keypoints3D[0]; // Wrist
  const indexBase = keypoints3D[5]; // Index finger base
  const pinkyBase = keypoints3D[17]; // Pinky base

  // Forearm direction vector (wrist → midpoint of knuckles)
  const forearmVector = {
    x: (indexBase.x + pinkyBase.x) / 2 - wrist.x,
    y: (indexBase.y + pinkyBase.y) / 2 - wrist.y,
    z: (indexBase.z + pinkyBase.z) / 2 - wrist.z,
  };

  // Knuckle line vector (index → pinky)
  const knuckleVector = {
    x: pinkyBase.x - indexBase.x,
    y: pinkyBase.y - indexBase.y,
    z: pinkyBase.z - indexBase.z,
  };

  // Palm normal = forearm × knuckle (direction the palm faces)
  const palmNormal = {
    x: forearmVector.y * knuckleVector.z - forearmVector.z * knuckleVector.y,
    y: forearmVector.z * knuckleVector.x - forearmVector.x * knuckleVector.z,
    z: forearmVector.x * knuckleVector.y - forearmVector.y * knuckleVector.x,
  };

  const normalLength = Math.sqrt(
    palmNormal.x * palmNormal.x +
      palmNormal.y * palmNormal.y +
      palmNormal.z * palmNormal.z,
  );
  if (normalLength === 0) return 0;

  return Math.atan2(palmNormal.x / normalLength, palmNormal.z / normalLength);
};
